import { createRouter, createWebHistory, LocationQuery } from 'vue-router';
import { getActiveHowl, stopAllSounds } from '@ilteducation/audio-resources';
import { useUserContext } from '@ilteducation/ilt-auth';
import isArray from 'lodash/isArray';
import config from './config/ConfigProvider';
import useSubscriptions from './foundations/helpers/use-subscriptions';
import { runVersionCheckForRoute } from './foundations/helpers/version-check';

// lazy loading
const EmptyRouterView = () => import('./views/EmptyRouterView.vue');
const Login = () => import('./views/Login.vue');
const SecureLogin = () => import('./views/SecureLogin.vue');
const LoginSSO = () => import('./views/LoginSSO.vue');
const Landing = () => import('./views/Landing.vue');
const MyPage = () => import('./views/MyPage.vue');
const UserPage = () => import('./views/UserPage.vue');
const VideoPage = () => import('./views/VideoPage.vue');
const Videos = () => import('./views/Videos.vue');
const SubjectPage = () => import('./views/SubjectPage.vue');
const Guides = () => import('./views/Guides.vue');
const SingleVideo = () => import('./views/SingleVideo.vue');
const AreaGuide = () => import('./views/AreaGuide.vue');
const Materials = () => import('./views/Materials.vue');
const MaterialLandingPage = () => import('./views/MaterialLandingPage.vue');
const NotFound = () => import('./views/NotFound.vue');
const NoAccess = () => import('./views/NoAccess.vue');
const Forbidden = () => import('./views/Forbidden.vue');
const PublicVideos = () => import('./views/PublicVideos.vue');
const ControlPanel = () => import('./views/ControlPanel.vue');
const WordOfTheDayGame = () => import('./views/WordOfTheDayGame.vue');
const SkeletorGame = () => import('./views/SkeletorGame.vue');
const PlanetEarthGame = () => import('./views/PlanetEarthGame.vue');
const MatchThePairsGame = () => import('./views/MatchThePairsGame.vue');
const GamesPage = () => import('./views/GamesPage.vue');
const CreateQuizter = () => import('./views/quizter/CreateQuizter.vue');
const HostGame = () => import('./views/quizter/HostGame.vue');
const QuizterHomePage = () => import('./views/quizter/QuizterHomePage.vue');
const QuizterCreatePage = () => import('./views/quizter/QuizterCreatePage.vue');
const QuizterMyPage = () => import('./views/quizter/QuizterMyPage.vue');
const QuizterSharedWithMePage = () => import('./views/quizter/QuizterSharedWithMePage.vue');
const QuizterEditPage = () => import('./views/quizter/QuizterEditPage.vue');
const SharedQuizterViewPage = () => import('./views/quizter/SharedQuizterViewPage.vue');

const Collection = () => import('./views/Collection.vue');

const basePath = config.value('basePath');

const router = createRouter({
  scrollBehavior: (to, from, savedPosition) => {
    if (to.path === from.path && !to.hash) {
      // Query-only change, e.g. selecting a search filter: keep the scroll position
      return false;
    }
    if (savedPosition && to.name === 'Videos' && from.name === 'showFilm') {
      localStorage.setItem('videoScrollTop', JSON.stringify(savedPosition.top));
      return savedPosition;
    }
    if (to.hash) {
      return {
        el: to.hash,
        behavior: 'smooth',
        top: 64, // header height
      };
    }
    return { left: 0, top: 0 };
  },
  history: createWebHistory(basePath || '/'),
  strict: true,
  routes: [
    {
      path: '/',
      redirect: (to) => {
        const userContext = useUserContext();
        if (userContext.isLoggedIn) {
          return { name: 'begreppaLanding' };
        }
        return { name: 'login', query: to.query };
      },
    },
    {
      path: '/public',
      name: 'public',
      component: PublicVideos,
    },
    {
      path: '/quizter',
      children: [
        {
          path: 'create/:videoId',
          name: 'CreateQuizter',
          component: CreateQuizter,
        },
        {
          path: 'home',
          name: 'QuizterHomePage',
          component: QuizterHomePage,
        },
        {
          path: 'create',
          name: 'QuizterCreatePage',
          component: QuizterCreatePage,
        },
        {
          path: 'my',
          name: 'QuizterMyPage',
          component: QuizterMyPage,
        },
        {
          path: 'shared',
          name: 'QuizterSharedWithMePage',
          component: QuizterSharedWithMePage,
        },
        {
          path: 'shared/:shareId',
          name: 'SharedQuizterViewPage',
          component: SharedQuizterViewPage,
        },
        {
          path: 'edit/:activityId',
          name: 'QuizterEditPage',
          component: QuizterEditPage,
        },
        {
          path: 'host/:gameId',
          name: 'HostQuizter',
          component: HostGame,
        },
      ],
      beforeEnter: (_to, _from, next) => {
        const userContext = useUserContext();

        if (userContext.hasRole('employee')) {
          next();
        } else {
          next({ name: 'Videos' });
        }
      },
      meta: {
        requiresAuth: true,
        hideBars: 'mobile',
      },
    },
    {
      path: '/noaccess',
      name: 'noaccess',
      component: NoAccess,
    },
    {
      path: '/forbidden',
      name: 'forbidden',
      component: Forbidden,
    },
    {
      path: '/login',
      name: 'login',
      component: Login,
    },
    {
      path: '/login/:loginHint',
      name: 'loginSSO',
      component: LoginSSO,
      props: true,
      beforeEnter: (_to, _from, next) => {
        const userContext = useUserContext();
        if (userContext.isLoggedIn) {
          next({ name: 'begreppaLanding' });
        } else {
          next();
        }
      },
    },
    {
      path: '/login/secure',
      name: 'SecureLogin',
      component: SecureLogin,
    },
    {
      path: '/control-panel',
      name: 'ControlPanel',
      component: ControlPanel,
      beforeEnter: (_to, _from, next) => {
        const context = useUserContext();
        if (context.isSuperUser || context.hasRole('employee')) {
          next();
        } else {
          next({ name: 'begreppaLanding' });
        }
      },
    },
    {
      path: '',
      name: 'begreppaLanding',
      component: Landing,
      meta: {
        requiresAuth: true,
      },
      redirect: {
        name: 'Videos',
      },
      children: [
        {
          path: '/videos',
          name: 'Videos',
          component: Videos,
          meta: {
            requiresAuth: true,
          },
        },
        {
          path: '/guides',
          name: 'Guides',
          component: Guides,
        },
        {
          path: '/materials',
          children: [
            {
              path: '',
              name: 'Materials',
              component: MaterialLandingPage,
            },
            {
              path: 'search',
              name: 'materialsSearch',
              component: Materials,
            },
          ],
        },
        {
          path: '/games',
          name: 'Games',
          component: GamesPage,
          meta: {
            requiresAuth: true,
          },
        },
        {
          path: '/games/word-of-the-day',
          name: 'GamesWordOfTheDay',
          component: WordOfTheDayGame,
          beforeEnter: (_to, _from, next) => {
            const tooNarrowScreen = window.innerWidth < 600; // equivalent to 'xs' in vuetify
            if (tooNarrowScreen) {
              next({ name: 'Games' });
            } else {
              next();
            }
          },
          meta: {
            requiresAuth: true,
          },
        },
        {
          path: '/games/humanbody',
          name: 'GamesSkeletor',
          component: SkeletorGame,
          meta: {
            requiresAuth: true,
          },
        },
        {
          path: '/games/planetearth',
          name: 'GamesPlanetEarth',
          component: PlanetEarthGame,
          meta: {
            requiresAuth: true,
          },
        },
        {
          path: '/games/match-the-pairs/:videoId',
          name: 'GamesMatchThePairs',
          component: MatchThePairsGame,
          meta: {
            requiresAuth: true,
            hideBars: 'always',
          },
        },
        {
          path: '/word-of-the-day',
          redirect: { name: 'GamesWordOfTheDay' },
        },
        {
          path: '/video/:videoId',
          name: 'VideoPage',
          component: VideoPage,
          meta: {
            requiresAuth: true,
          },
        },
        {
          path: '/videos/:subject',
          name: 'SubjectPage',
          component: SubjectPage,
          meta: {
            requiresAuth: true,
          },
        },
        {
          path: '/videos/:subject/:area',
          name: 'videos.AreaGuide',
          component: AreaGuide,
          meta: {
            requiresAuth: true,
          },
        },
        {
          path: '/goto/videos/:subject/:area/:film',
          meta: {
            requiresAuth: true,
          },
          redirect: (to) => ({ name: 'showFilm', query: to.query }),
        },
        {
          path: '/videos/:subject/:area/:film',
          name: 'showFilm',
          component: SingleVideo,
          meta: {
            requiresAuth: true,
          },
        },
        {
          path: '/begreppa/:rest+',
          redirect: (to) => ({
            path: router.resolve(isArray(to.params.rest) ? to.params.rest.join('/') : to.params.rest).href,
          }),
          meta: {
            requiresAuth: true,
          },
        },
        {
          path: '/my-page',
          name: 'myPage',
          component: MyPage,
          meta: {
            requiresAuth: true,
          },
        },
        {
          path: '/collection/:id',
          name: 'collection',
          component: Collection,
        },
      ],
    },
    {
      path: '/users',
      component: EmptyRouterView,
      children: [
        {
          path: ':userUid',
          name: 'users.person',
          component: UserPage,

          meta: {
            requiresAuth: true,
          },
        },
      ],
    },
    {
      name: 'notFound',
      path: '/:pathMatch(.*)*',
      component: NotFound,
    },
  ],
});

// Before the auth guard, which returns early in several branches. False aborts the navigation so the reload replaces it.
router.beforeEach(async (to) => {
  return (await runVersionCheckForRoute(to)) !== 'reload';
});

router.beforeEach((to, from, next) => {
  if (getActiveHowl()) {
    stopAllSounds();
  }

  const userContext = useUserContext();
  const subscriptions = useSubscriptions();
  if (to.meta.requiresAuth && !userContext.isLoggedIn) {
    const query: LocationQuery = {};
    if (typeof to.query.reauthenticate !== 'undefined') {
      query.reauthenticate = 'auto';
    }
    localStorage.setItem('savedRouteOnLogin', JSON.stringify(to.fullPath));
    next({ name: 'login', query });
    return;
  } else if (to.meta.requiresAuth && userContext.isLoggedIn && subscriptions.value.length === 0) {
    next({ name: 'noaccess' });
    return;
  }

  next();
});

export default router;
